import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AdminSubscriptionItemDialogComponent } from '../admin-subscription-item-dialog/admin-subscription-item-dialog.component';
import { HttpClient, HttpParams } from '@angular/common/http';
import { User } from '../Models/User';
import { SubscriptionItem } from '../Models/Subscription';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { CharityCategory } from '../Models/CharityCategory';

interface RegionVm {
  id: number;
  code: string;
  name: string;
}

@Component({
  selector: 'app-admin-subscription-items',
  templateUrl: './admin-subscription-items.component.html',
  styleUrls: ['./admin-subscription-items.component.css'],
  standalone: false

})
export class AdminSubscriptionItemsComponent {

  @Input() subscriptionId!: number;
  @Input() serviceTypeId!: number;
  @Input() loggedInUser?: User;

  private loggedInUserTag = "";
  items: any[] = [];
  regions: RegionVm[] = [];
  charityCategories: CharityCategory[] = [];

  // Per-row region popup state (Named subscriptions)
  isRowRegionPopupOpen = false;
  rowRegionPopupItem: any = null;
  rowSelectedRegions: RegionVm[] = [];
  rowRegionSearchTerm = '';
  rowFilteredRegions: RegionVm[] = [];
  rowShowSelectedOnly = false;
  itemRegionIds: Record<number, number[]> = {};

  // Tooltip state for alias / previous names popup
  tooltipVisible: Record<number, boolean> = {};
  tooltipContent: Record<number, { aliases: string[]; previousNames: string[] }> = {};
  tooltipPosition: Record<number, { top: number; left: number }> = {};
  private tooltipHideTimeouts: { [key: number]: any } = {};
  private isMouseOverTooltip: { [key: number]: boolean } = {};

  constructor(private http: HttpClient, private dialog: MatDialog) { }

  ngOnInit() { 
    if (this.subscriptionId >0) {
      this.loggedInUserTag = `${this.loggedInUser?.lastName}, ${this.loggedInUser?.firstName}`;
      this.getRegions();
      this.getCharityCategories();
      this.getSubscriptionItems();
    }
  }

  getSubscriptionItems() {
    const parameters = new HttpParams()
      .set('subscriptionId', this.subscriptionId)
      .set('user', this.loggedInUserTag);

    // named beneficiaries for serviceType1
    if (this.serviceTypeId ===1) {
      this.http.get<any[]>('/charitycentral/GetBeneficiariesForSubscription', { params: parameters })
        .subscribe(results => {
          this.items = (results || []).map(r => this.normalizeTooltipFields(r)).map((it, idx) => ({ ...it, _color: this.getColorForIndex(idx) }));
          this.loadAllBeneficiaryRegions();
        });
    } else if (this.serviceTypeId ===2) {
      // Discretionary: categories + regions
      this.http.get<any[]>('/charitycentral/GetAllSubscriptionItems', { params: parameters })
        .subscribe(results => this.items = (results || []).filter(x => !x.beneficiaryId).map(r => this.normalizeTooltipFields(r)).map((it, idx) => ({ ...it, _color: this.getColorForIndex(idx) })));
    } else if (this.serviceTypeId ===3) {
      // Trust: same as discretionary but only categories matter — filter out region-only items
      this.http.get<any[]>('/charitycentral/GetAllSubscriptionItems', { params: parameters })
        .subscribe(results => this.items = (results || []).filter(x => !x.beneficiaryId).filter(x => x.charityCategoryId != null).map(r => this.normalizeTooltipFields(r)).map((it, idx) => ({ ...it, _color: this.getColorForIndex(idx) })));
    } else {
      // fallback - load all subscription items and annotate
      this.http.get<any[]>('/charitycentral/GetAllSubscriptionItems', { params: parameters })
        .subscribe(results => this.items = (results || []).map(r => this.normalizeTooltipFields(r)).map((it, idx) => ({ ...it, _color: this.getColorForIndex(idx) })));
    }
  }

  // Ensure aliases and previousNames are arrays for consistent tooltip rendering
  private normalizeTooltipFields(r: any) {
    const item = { ...(r || {}) };
    if (!Array.isArray(item.aliases)) {
      if (typeof item.aliases === 'string' && item.aliases.trim().length >0) {
        item.aliases = item.aliases.split(',').map((s: string) => s.trim()).filter((s: string) => s.length >0);
      } else {
        item.aliases = [];
      }
    }

    if (!Array.isArray(item.previousNames)) {
      if (typeof item.previousNames === 'string' && item.previousNames.trim().length >0) {
        item.previousNames = item.previousNames.split(',').map((s: string) => s.trim()).filter((s: string) => s.length >0);
      } else {
        item.previousNames = [];
      }
    }

    return item;
  }

  // ---------------- REGIONS (Code → Number) ----------------
  getRegions() {
    const params = new HttpParams().set('user', this.loggedInUserTag);
    this.http.get<{ id: number; code: string; name: string }[]>('/charitycentral/getallregions', { params })
      .subscribe({
        next: (res) => { this.regions = res; },
        error: err => console.error('Region load failed', err)
      });
  }

  getRegionNameById(regionId: number) {
    return this.regions.find(r => r.id === regionId)?.name || 'Unknown Region';
  }

  // ---------------- CHARITY CATEGORIES ----------------
  getCharityCategories() {
    this.http.get<CharityCategory[]>('/charitycentral/getcharitycategories')
      .subscribe({
        next: (res) => {
          this.charityCategories = res || [];
        },
        error: err => {
          console.error('Failed to load charity categories', err);
          this.charityCategories = [];
        }
      });
  }

  getCategoryTextById(id: number) {
    const c = this.charityCategories.find(x => x.id === id);
    return c ? `${c.parent} - ${c.child}` : 'Unknown Category';
  }

  // Return subscribed category texts sorted alphabetically (unique)
  getSubscribedCategoryTextsSorted(): string[] {
    const texts = (this.items || [])
      .filter(i => i && i.charityCategoryId != null)
      .map(i => this.getCategoryTextById(i.charityCategoryId));

    // unique
    const unique = Array.from(new Set(texts));
    return unique.sort((a, b) => a.localeCompare(b));
  }

  // Return subscribed region names sorted alphabetically (unique)
  getSubscribedRegionNamesSorted(): string[] {
    const names = (this.items || [])
      .filter(i => i && i.regionId != null)
      .map(i => this.getRegionNameById(i.regionId));

    const unique = Array.from(new Set(names));
    return unique.sort((a, b) => a.localeCompare(b));
  }

  openAddDialog(mode?: 'category' | 'region') {
    console.log({ serviceTypeId: this.serviceTypeId, subscriptionId: this.subscriptionId, openMode: mode, loggedInUser: this.loggedInUser, items: this.items })
    const dialogRef = this.dialog.open(AdminSubscriptionItemDialogComponent, {
      maxWidth: '95vw',
      maxHeight:'75vh',
      panelClass: 'subscription-item-dialog',
      data: { serviceTypeId: this.serviceTypeId, subscriptionId: this.subscriptionId, openMode: mode, loggedInUser: this.loggedInUser, items: this.items }
    });


    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('New Subscription Item:', result);
      }

      // Always refresh list
      this.getSubscriptionItems();
    });

  }

  // ---- Named subscription: per-row region popup ----

  loadAllBeneficiaryRegions() {
    const params = new HttpParams()
      .set('subscriptionId', this.subscriptionId)
      .set('user', this.loggedInUserTag);
    this.http.get<Record<number, number[]>>('/charitycentral/getallbeneficiaryregionsforsubscription', { params })
      .subscribe({
        next: (result) => { this.itemRegionIds = result || {}; },
        error: () => { this.itemRegionIds = {}; }
      });
  }

  getItemRegionNames(item: any): string {
    const ids: number[] = this.itemRegionIds[item.id] || [];
    return ids.map(id => this.regions.find(r => r.id === id)?.name || '').filter(n => n).join(', ');
  }

  getItemRegionCount(item: any): number {
    return (this.itemRegionIds[item.id] || []).length;
  }

  openRowRegionPopup(item: any) {
    this.rowRegionPopupItem = item;
    const currentIds: number[] = this.itemRegionIds[item.id] || [];
    this.rowSelectedRegions = this.regions.filter(r => currentIds.includes(r.id));
    this.rowRegionSearchTerm = '';
    this.rowShowSelectedOnly = false;
    this.rowFilteredRegions = [...this.regions];
    this.isRowRegionPopupOpen = true;
    setTimeout(() => {
      const input = document.querySelector('.row-region-search-input') as HTMLInputElement;
      if (input) input.focus();
    }, 50);
  }

  closeRowRegionPopup() {
    this.isRowRegionPopupOpen = false;
    this.rowRegionPopupItem = null;
    this.rowSelectedRegions = [];
    this.rowRegionSearchTerm = '';
    this.rowFilteredRegions = [];
    this.rowShowSelectedOnly = false;
  }

  filterRowRegions() {
    const source = this.rowShowSelectedOnly ? this.rowSelectedRegions : this.regions;
    const term = this.rowRegionSearchTerm.toLowerCase();
    this.rowFilteredRegions = term ? source.filter(r => r.name.toLowerCase().includes(term)) : [...source];
  }

  toggleSelectAllRowRegions(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.rowSelectedRegions = checked ? [...this.regions] : [];
    this.filterRowRegions();
  }

  toggleRowRegion(region: RegionVm) {
    const idx = this.rowSelectedRegions.findIndex(r => r.id === region.id);
    if (idx > -1) this.rowSelectedRegions.splice(idx, 1);
    else this.rowSelectedRegions.push(region);
    if (this.rowShowSelectedOnly) this.filterRowRegions();
  }

  isRowRegionSelected(region: RegionVm): boolean {
    return this.rowSelectedRegions.some(r => r.id === region.id);
  }

  saveRowRegions() {
    const item = this.rowRegionPopupItem;
    if (!item) return;
    const regionIds = this.rowSelectedRegions.map(r => r.id);

    let params = new HttpParams()
      .set('subscriptionItemId', item.id)
      .set('user', this.loggedInUserTag);
    regionIds.forEach(id => params = params.append('regionIds', id.toString()));

    this.http.post('/charitycentral/saveregionsforbeneficiaryitem', null, { params }).subscribe({
      next: () => {
        this.closeRowRegionPopup();
        this.getSubscriptionItems();
      },
      error: err => console.error('Failed to save regions', err)
    });
  }

  deleteItem(item: any) {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Delete Subscription Item', message: 'Are you sure you want to delete this subscription item?', confirmText: 'Yes', cancelText: 'No' }
    });

    ref.afterClosed().subscribe(result => {
      if (!result) return;

      // If local/unsaved item (no server id), remove client-side
      if (!item.id || item.id <=0) {
        this.items = this.items.filter(i => i !== item);
        return;
      }

      const params = new HttpParams()
        .set('subscriptionItemId', item.id)
        .set('user', this.loggedInUserTag);

      this.http.post<boolean>('/charitycentral/deletesubscriptionitem', null, { params })
        .subscribe({
          next: (success) => {
            if (success) {
              this.getSubscriptionItems();
            } else {
              console.error('Failed to delete subscription item (server returned false).');
            }
          },
          error: (err) => {
            console.error('Error deleting subscription item:', err);
          }
        });
    });
  }


  formatAddress(item: any): string {
    return [
      item.addressLine1,
      item.addressLine2,
      item.addressLine3,
      item.addressLine4,
      item.addressLine5,
      item.postCode
    ]
      .filter(v => v != null && String(v).trim() !== '')
      .join(', ');
  }

  // Tooltip helpers (same behaviour as reporting/dialog)
  showTooltip(index: number, aliases: string[], previousNames: string[], cell: HTMLElement) {
    if (this.tooltipHideTimeouts[index]) {
      clearTimeout(this.tooltipHideTimeouts[index]);
      this.tooltipHideTimeouts[index] = null;
    }

    this.tooltipVisible[index] = true;
    this.tooltipContent[index] = {
      aliases: aliases || [],
      previousNames: previousNames || []
    };

    const rect = cell.getBoundingClientRect();
    const offset =8;
    const tooltipWidth =550; // match reporting
    const maxItems = Math.max(aliases?.length ||0, previousNames?.length ||0);
    const tooltipHeight = Math.min(maxItems *30 +80,400);

    // Prefer below the row
    let top = rect.bottom + offset;
    if (top + tooltipHeight > window.innerHeight -20) {
      top = rect.top - tooltipHeight - offset;
    }

    let left = rect.left;
    const maxLeft = window.innerWidth - tooltipWidth -20;
    if (left > maxLeft) left = maxLeft;
    if (left <8) left =8;

    this.tooltipPosition[index] = { top, left };
  }

  hideTooltip(index: number) {
    this.tooltipHideTimeouts[index] = setTimeout(() => {
      if (!this.isMouseOverTooltip[index]) {
        this.tooltipVisible[index] = false;
      }
    },150);
  }

  onTooltipMouseEnter(index: number) {
    this.isMouseOverTooltip[index] = true;
    if (this.tooltipHideTimeouts[index]) {
      clearTimeout(this.tooltipHideTimeouts[index]);
      this.tooltipHideTimeouts[index] = null;
    }
  }

  onTooltipMouseLeave(index: number) {
    this.isMouseOverTooltip[index] = false;
    this.tooltipVisible[index] = false;
  }

  // Counts for category and region subscribed items
  getCategoryCount(): number {
    return (this.items || []).filter(i => i && i.charityCategoryId != null).length;
  }

  getRegionCount(): number {
    return (this.items || []).filter(i => i && i.regionId != null).length;
  }

  // assign simple color palette cycling for items
  private getColorForIndex(i: number): string {
    const palette = ['#4f46e5', '#16a34a', '#7c3aed', '#2563eb', '#ea580c'];
    return palette[i % palette.length];
  }

}
